import type { CallCenterFeatureOptions } from '@/features/call-center'
import type { Id } from '@/infra/id'
import { memoize } from '@/utils/memoize-util'
import { ValueRetrievablePromise } from '@/utils/value-retrievable-promise'
import { CallCenterViewModel } from './CallCenterViewModel'
import { CallCenterJobPageViewModel } from './CallCenterJobPageViewModel'

/**
 * Function to acquire the call center's view model.
 */
export type AcquireCallCenterViewModelFn = (
  signal?: AbortSignal,
) => Promise<CallCenterViewModel>

/**
 * Function to acquire the view model that loads a job into the call center.
 */
export type AcquireCallCenterJobPageViewModelFn = (
  jobId: Id,
  signal?: AbortSignal,
) => Promise<CallCenterJobPageViewModel>

export function InternalCallCenterFeature(options: CallCenterFeatureOptions) {
  const acquireCallCenterViewModel: AcquireCallCenterViewModelFn = memoize(
    async (signal?: AbortSignal) => {
      signal?.throwIfAborted()

      const { createJobCreationRootViewModel, createJobCheckoutViewModel } =
        await ValueRetrievablePromise.props({
          createJobCreationRootViewModel:
            options.createJobCreationRootViewModel,
          createJobCheckoutViewModel: options.createJobCheckoutViewModel,
        })

      return new CallCenterViewModel(
        options.searchCallCenterContacts,
        options.searchMembers,
        options.fetchMember,
        options.createMemberManually,
        options.createCustomer,
        options.searchJobs,
        options.emailCheckoutLink,
        options.getCurrentUser,
        options.getUserData,
        options.getCurrentUserWorkspaceMemberships,
        options.listUserWorkspaceMemberships,
        options.fetchWorkspace,
        options.submitJobRequest,
        options.applyJobCouponByCode,
        options.removeJobCouponByCode,
        options.publishedLegend,
        createJobCreationRootViewModel(),
        createJobCheckoutViewModel,
        options.generateCheckoutLink,
        options.invalidateCheckoutSessions,
        options.newAlert,
        options.createAlertErrorHandler,
        options.createAlertRetryWrapper,
        options.navigate,
      )
    },
    { promise: ([signal]) => signal },
  )

  const acquireCallCenterJobPageViewModel: AcquireCallCenterJobPageViewModelFn =
    memoize(
      async (jobId: Id, signal?: AbortSignal) => {
        const callCenterVm = await acquireCallCenterViewModel(signal)
        return new CallCenterJobPageViewModel(
          jobId,
          options.fetchJob,
          callCenterVm.jobVm,
          callCenterVm.setJobId,
        )
      },
      {
        // Only the open job's page is kept, so a revalidating route reuses it
        // while opening another job starts afresh.
        maxCount: 1,
        promise: ([, signal]) => signal,
      },
    )

  return {
    // Internal
    acquireCallCenterViewModel,
    acquireCallCenterJobPageViewModel,
  }
}
